<template>
  <section class="university-partners-section">
    <div class="partners-wrapper">

      <!-- Section Header -->
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">
            Our <span class="gradient-text">University Partners</span>
          </h2>
          <p class="section-subtitle">
            Collaborating with leading universities across India to deliver industry-integrated education.
          </p>
        </div>
      </Motion>

      <!-- Partners Grid -->
      <div class="partners-grid">
        <Motion
          v-for="(uni, i) in universities"
          :key="i"
          :initial="{ opacity: 0, y: 40 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :inViewOptions="{ once: true, margin: '-80px' }"
          :transition="{ duration: 0.7, delay: i * 0.1, ease: 'easeOut' }"
        >
          <article class="partner-card" :style="{ '--accent': uni.accent, '--accent-rgb': uni.accentRgb }">
            <div class="partner-top-bar" />

          <!-- Media area — infrastructure image -->
          <div class="partner-media">
            <img class="media-img" :src="uni.infrastructureImage" :alt="uni.name" loading="lazy" />
            <!-- Bottom gradient mask blending into the card body -->
            <div class="media-overlay" />
          </div>

          <!-- Header: Logo + Title -->
          <div class="partner-header">
            <div class="partner-logo-wrap">
              <img :src="uni.image" :alt="uni.name" class="partner-logo" loading="lazy" />
            </div>
            <div class="partner-title-block">
              <h3 class="partner-name">{{ uni.name }}</h3>
              <span class="partner-short">{{ uni.shortName }}</span>
            </div>
          </div>

          <!-- Meta badges -->
          <div class="partner-meta">
            <span class="meta-badge location-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {{ uni.location }}
            </span>
            <span class="meta-badge type-badge" :class="{ 'online': uni.type === 'Online' }">
              <svg v-if="uni.type === 'Online'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/><path d="M12 6v6l4 2"/></svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              {{ uni.type }}
            </span>
          </div>

          <!-- Description -->
          <p class="partner-desc">{{ uni.description }}</p>

          <!-- Accreditations -->
          <div v-if="uni.accreditations.length" class="partner-accreditations">
            <span class="accreditation-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.56 2.75 9.56 2 10.56 2.75"/><polyline points="15.41 2.75 16.41 2 17.41 2.75"/><path d="M9.59 21.75a1 1 0 0 0 1.41 0l2-2a1 1 0 0 0 0-1.41l-2-2a1 1 0 0 0-1.41 0l-2 2a1 1 0 0 0 0 1.41z"/><path d="M14.41 21.75a1 1 0 0 0 1.41 0l2-2a1 1 0 0 0 0-1.41l-2-2a1 1 0 0 0-1.41 0l-2 2a1 1 0 0 0 0 1.41z"/></svg>
              Accreditations
            </span>
            <div class="accreditation-chips">
              <span v-for="acc in uni.accreditations" :key="acc" class="accreditation-chip">{{ acc }}</span>
            </div>
          </div>

          <!-- Programs -->
          <div class="partner-programs">
            <span class="programs-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
              Programs Offered
            </span>
            <div class="program-chips">
              <span v-for="prog in uni.programs" :key="prog" class="program-chip">{{ prog }}</span>
            </div>
          </div>

          <!-- CTA -->
          <a :href="uni.website" target="_blank" rel="noopener noreferrer" class="partner-cta">
            Visit Website
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </article>
        </Motion>
      </div>

    </div>
  </section>
</template>

<script setup lang="ts">
// ── Data ─────────────────────────────────────────────────────────────────────
const universities = [
  {
    name: 'Lamrin Tech Skills University Punjab',
    shortName: 'LTSU',
    image: '/images/ltsu_logo.webp',
    infrastructureImage: '/universities/ltsu-back.webp',
    location: 'Ropar, Punjab',
    type: 'On-Campus',
    description: 'A state-recognized university focused on skill-based education, offering industry-aligned programs in technology, engineering, and management.',
    accreditations: ['UGC Recognised', 'Punjab State Act No. 22 of 2021'],
    programs: ['B.Tech', 'M.Tech', 'BCA', 'MCA', 'MBA'],
    website: 'https://ltsu.ac.in/',
    accent: '#3b82f6',
    accentRgb: '59,130,246',
  },
  {
    name: 'Sandip University',
    shortName: 'Sandip',
    image: '/images/sandip.png',
    infrastructureImage: '/universities/sandip-university.jpg',
    location: 'Maharashtra',
    type: 'On-Campus',
    description: 'A multi-disciplinary university offering industry-aligned programs in engineering, management, pharmacy, and science.',
    accreditations: [],
    programs: ['B.Tech', 'M.Tech', 'MBA'],
    website: 'https://www.sandipuniversity.edu.in/',
    accent: '#8b5cf6',
    accentRgb: '139,92,246',
  },
  {
    name: 'Canara Engineering College',
    shortName: 'Canara',
    image: '/images/canara.png',
    infrastructureImage: '/universities/canara-college.webp',
    location: 'Karnataka',
    type: 'On-Campus',
    description: 'A premier institution offering quality programs in technology and engineering with strong industry connections.',
    accreditations: [],
    programs: ['B.Tech', 'MBA', 'MCA'],
    website: 'https://www.canaraengineering.in/',
    accent: '#ec4899',
    accentRgb: '236,72,153',
  },
  {
    name: 'Sanjay Ghodawat University',
    shortName: 'SGU',
    image: '/images/SGU.png',
    infrastructureImage: '/universities/sanjay-university.webp',
    location: 'Kolhapur, Maharashtra',
    type: 'On-Campus',
    description: 'A modern university offering diverse programs in technology, management, and sciences with a focus on practical education.',
    accreditations: [],
    programs: ['B.Tech', 'MBA', 'MCA'],
    website: 'https://www.sguk.ac.in/',
    accent: '#10b981',
    accentRgb: '16,185,129',
  },
  {
    name: 'Joy University',
    shortName: 'Joy',
    image: '/images/joy.png',
    infrastructureImage: '/universities/joy-university.webp',
    location: 'Tamil Nadu',
    type: 'On-Campus',
    description: 'Committed to providing quality higher education in engineering, management, and arts with strong placement support.',
    accreditations: [],
    programs: ['B.Tech', 'MBA', 'BCA', 'MCA'],
    website: 'https://www.joyuniversity.ac.in/',
    accent: '#f59e0b',
    accentRgb: '245,158,11',
  },
  {
    name: 'Jain University (JAIN Online)',
    shortName: 'Jain Online',
    image: '/jain-university.jpg',
    infrastructureImage: '/universities/jain-university.webp',
    location: 'Bengaluru, Karnataka',
    type: 'Online',
    description: 'NAAC A++ accredited university delivering rigorous online programs with industry-aligned curriculum and placement support.',
    accreditations: ['NAAC A++', 'UGC Recognised', 'AICTE Approved'],
    programs: ['MBA Online', 'MCA Online', 'M.Tech Online'],
    website: 'https://www.onlinejain.com/',
    accent: '#14b8a6',
    accentRgb: '20,184,166',
  },
]
</script>

<style scoped>
/* ── Section ─────────────────────────────────────────────────────────────── */
.university-partners-section {
  padding: 8rem 2rem;
  position: relative;
  z-index: 10;
}

.partners-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 4.5rem;
}

.section-title {
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.75;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Grid ─────────────────────────────────────────────────────────────────── */
.partners-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
.partner-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  transition:
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.32s ease,
    box-shadow 0.32s ease;
}

.partner-card:hover {
  transform: translateY(-6px);
  border-color: rgba(var(--accent-rgb), 0.38);
  box-shadow:
    0 8px 28px rgba(var(--accent-rgb), 0.08),
    0 0 0 1px rgba(var(--accent-rgb), 0.06);
}

/* ── Accent bar ───────────────────────────────────────────────────────────── */
.partner-top-bar {
  height: 3px;
  background: linear-gradient(90deg, var(--accent), rgba(var(--accent-rgb), 0.25));
  flex-shrink: 0;
}

/* ── Media (infrastructure image) ────────────────────────────────────────────── */
.partner-media {
  position: relative;
  height: 160px;
  overflow: hidden;
  flex-shrink: 0;
}

.media-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
}

.partner-card:hover .media-img {
  transform: scale(1.06);
}

/* Bottom gradient mask blending into the card body */
.media-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) 40%,
      rgba(6, 5, 24, 0.65) 100%);
  z-index: 1;
  pointer-events: none;
}

[data-theme="light"] .media-overlay {
  background: linear-gradient(180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) 40%,
      rgba(255, 255, 255, 0.85) 100%);
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.partner-header {
  padding: 1.2rem 1.55rem 0.8rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.partner-logo-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(var(--accent-rgb), 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.partner-card:hover .partner-logo-wrap {
  transform: scale(1.05);
  box-shadow: 0 4px 14px rgba(var(--accent-rgb), 0.2);
}

.partner-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.partner-title-block {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.partner-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.35;
}

.partner-short {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--accent);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

/* ── Meta badges ──────────────────────────────────────────────────────────── */
.partner-meta {
  padding: 0 1.55rem 0.8rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.meta-badge svg {
  flex-shrink: 0;
}

.location-badge {
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.18);
  color: var(--accent);
}

.type-badge {
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.18);
  color: var(--accent);
}

.type-badge.online {
  background: rgba(20, 184, 166, 0.08);
  border-color: rgba(20, 184, 166, 0.25);
  color: #14b8a6;
}

/* ── Description ──────────────────────────────────────────────────────────── */
.partner-desc {
  padding: 0 1.55rem;
  font-size: 0.84rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 0 0 0.9rem;
}

/* ── Accreditations ───────────────────────────────────────────────────────── */
.partner-accreditations {
  padding: 0 1.55rem 0.7rem;
}

.accreditation-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin: 0 0 0.55rem;
}

.accreditation-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.accreditation-chip {
  padding: 0.28rem 0.65rem;
  border-radius: 50px;
  background: rgba(var(--accent-rgb), 0.07);
  border: 1px solid rgba(var(--accent-rgb), 0.18);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-secondary);
  letter-spacing: 0.01em;
}

/* ── Programs ─────────────────────────────────────────────────────────────── */
.partner-programs {
  padding: 0 1.55rem 0.8rem;
}

.programs-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin: 0 0 0.55rem;
}

.program-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.program-chip {
  padding: 0.28rem 0.65rem;
  border-radius: 50px;
  background: rgba(var(--accent-rgb), 0.07);
  border: 1px solid rgba(var(--accent-rgb), 0.18);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-secondary);
  letter-spacing: 0.01em;
}

/* ── CTA ────────────────────────────────────────────────────────────────────── */
.partner-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: auto 1.55rem 1.55rem;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--accent);
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
  border-radius: 50px;
  padding: 0.48rem 1.05rem;
  cursor: pointer;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition:
    background 0.26s ease,
    border-color 0.26s ease,
    gap 0.2s ease;
  align-self: flex-start;
}

.partner-cta:hover {
  background: rgba(var(--accent-rgb), 0.15);
  border-color: rgba(var(--accent-rgb), 0.42);
  gap: 0.58rem;
}

/* ── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 1100px) {
  .partners-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .university-partners-section {
    padding: 5rem 1rem;
  }

  .partners-grid {
    grid-template-columns: 1fr;
    gap: 1.1rem;
  }

  .section-title {
    font-size: clamp(1.85rem, 7vw, 2.6rem);
  }
}
</style>
